import { EventEmitter, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { isEmpty } from 'lodash';
import { ApiService, FeatureService, HTTP_CLIENT } from '@netfoundry-ui/shared/services';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { ExtensionService } from 'ziti-console-lib';

const DEFAULT_POLICY_CONFLICT_DETAILS_PAGE = 0;
const DEFAULT_POLICY_CONFLICT_DETAILS_SIZE = 999;
const DEFAULT_POLICY_CONFLICT_DETAILS_SORT = 'name,asc';

@Injectable({ providedIn: 'root' })
export class ServicePoliciesExtensionService implements ExtensionService {
    hosting = false;
    errors: any = {};
    closed: any = new EventEmitter<any>();
    consoleActionTriggered: any = new EventEmitter<any>();
    extensionEvent: EventEmitter<any> = new EventEmitter<any>();
    currentNetwork: any = {};

    formDataChanged: any = new BehaviorSubject<any>({ isEmpty: true });
    closeAfterSave = true;
    useDeleteSplitButton = true;
    moreActions: any[] = [];
    listActions: any[] = [];

    formData: any = {};
    conflictDetails: any = {};
    conflictPageData: any = null;

    constructor(
        private apiService: ApiService,
        private featureService: FeatureService,
        @Inject(ENVIRONMENT) private environment: Environment,
        @Inject(HTTP_CLIENT) private http: HttpClient,
    ) {
        this.apiService.currentNetwork.subscribe((network) => {
            this.currentNetwork = network;
        });
        this.consoleActionTriggered.subscribe(({ action, page, item }: any) => {
            if (action === 'edit' || !(window as any)?.gtag || !page) {
                return;
            }
            (window as any).gtag('event', `${action}_${page}`, {
                [`${page}_name`]: item?.name,
                [`${page}_id`]: item?.id,
            });
        });
    }

    extendOnInit(): void {
    }

    extendAfterViewInits(extentionPoints: any): void {
    }

    updateFormData(data: any): void {
        this.formData = data || {};
        if (!Array.isArray(this.formData.badges)) {
            this.formData.badges = [];
        }
        // Clear any previously replayed form patch so new/edit routes
        // don't inherit stale policy state from an earlier conflict update.
        this.formDataChanged.next({ isEmpty: true });
        this.extensionEvent.emit({
            type: 'formDataUpdated',
            data: {
                entityType: 'service-policy',
                formData: this.formData,
            },
        });
    }

    validateData(): Promise<any> {
        return Promise.resolve(true);
    }

    async emitEvent<TData, TResult>(event: any): Promise<TResult | undefined> {
        if (event?.type === 'tableDataUpdated' && event?.data?.resourceType === 'service-policies') {
            return await this.processServicePolicyTableData(event.data.results) as TResult;
        }

        this.extensionEvent.emit(event);
        return undefined;
    }

    formDataSaved(data: any): Promise<any> {
        return Promise.resolve(data);
    }

    processTableColumns(tableColumns: any): any[] {
        const updatedTableColumns = [...(tableColumns || [])];
        if (!updatedTableColumns.length) {
            return updatedTableColumns;
        }

        updatedTableColumns[0] = {
            ...updatedTableColumns[0],
            cellRendererParams: {
                ...(updatedTableColumns[0]?.cellRendererParams || {}),
                showIdentityIcons: true,
                getNamePrefix: (item: any) =>
                    item?.conflicted
                        ? {
                              show: true,
                              iconClass: 'icon-Network-Failure',
                              tooltip: 'Potential Conflict Detected',
                          }
                        : { show: false },
            },
        };

        return updatedTableColumns;
    }

    // Hook for the lib's list-table (lib-service-policies-list): adds the conflict marker to the Name cell.
    processListTableColumns(tableColumns: any[]): any[] {
        const columns = [...(tableColumns || [])];
        const nameCol = columns[0];
        if (nameCol) {
            const baseCellInputs = nameCol.cellInputs;
            columns[0] = {
                ...nameCol,
                cellInputs: (ctx: any) => {
                    const inputs = typeof baseCellInputs === 'function' ? baseCellInputs(ctx) : baseCellInputs || {};
                    return {
                        ...inputs,
                        cellConfig: {
                            ...(inputs.cellConfig || {}),
                            getNamePrefix: (item: any) =>
                                item?.conflicted
                                    ? { show: true, iconClass: 'icon-Network-Failure', tooltip: 'Potential Conflict Detected' }
                                    : { show: false },
                        },
                    };
                },
            };
        }
        return columns;
    }

    private async processServicePolicyTableData(results: any): Promise<any> {
        if (!this.currentNetwork?.id || !Array.isArray(results?.data) || results.data.length === 0) {
            return results;
        }

        const servicePolicyIds = results.data
            .map((row: any) => row?.id)
            .filter((id: string) => !isEmpty(id));

        if (!servicePolicyIds.length) {
            return results;
        }

        const conflictMap = new Map<string, boolean>();
        const requests: Promise<any>[] = [];
        const chunkSize = 30;

        for (let index = 0; index < servicePolicyIds.length; index += chunkSize) {
            requests.push(
                firstValueFrom(
                    this.getPolicyConflicts(this.currentNetwork.id, servicePolicyIds.slice(index, index + chunkSize))
                )
            );
        }

        try {
            const responses = await Promise.all(requests);
            responses.forEach((response: any) => {
                const conflictList = response?._embedded?.policyConflictList || [];
                conflictList.forEach((conflict: any) => {
                    if (!conflict?.servicePolicyId) {
                        return;
                    }
                    conflictMap.set(conflict.servicePolicyId, !!conflict.conflicted);
                });
            });
        } catch (error) {
            results.data = results.data.map((row: any) => ({
                ...row,
                conflicted: false,
            }));
            return results;
        }

        results.data = results.data.map((row: any) => ({
            ...row,
            conflicted: conflictMap.get(row?.id) || false,
        }));

        return results;
    }

    getPolicyConflicts(networkId: string, zitiIds: string[]): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        const params = new HttpParams()
            .set('networkId', networkId)
            .set('policyId', zitiIds.join(','));
        const protocol = this.environment.protocol || 'https';
        const url = `${protocol}://${this.environment.gatewayUrl}/conflict-detection/policy-conflicts`;

        return this.http.get(url, {
            headers,
            params,
            responseType: 'json',
        });
    }

    getPolicyConflictDetails(
        networkId: string,
        zitiId?: string,
        identityRoles: string[] = [],
        serviceRoles: string[] = [],
        semantic = 'AnyOf',
        policyType = 'Bind',
        page = DEFAULT_POLICY_CONFLICT_DETAILS_PAGE,
        size = DEFAULT_POLICY_CONFLICT_DETAILS_SIZE,
        sort = DEFAULT_POLICY_CONFLICT_DETAILS_SORT
    ): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json, application/json');
        const identityAttributeValue = this.normalizeAttributes(identityRoles).join(',');
        const serviceAttributeValue = this.normalizeAttributes(serviceRoles).join(',');
        let params = new HttpParams().set('networkId', networkId);

        if (zitiId) {
            params = params.set('policyId', zitiId);
        } else {
            params = params
                .set('identityAttribute', identityAttributeValue)
                .set('serviceAttribute', serviceAttributeValue)
                .set('semantic', semantic || 'AnyOf')
                .set('policyType', policyType || 'Bind');
        }

        params = params
            .set('page', `${page}`)
            .set('size', `${size}`)
            .set('sort', sort);

        const protocol = this.environment.protocol || 'https';
        const url = `${protocol}://${this.environment.gatewayUrl}/conflict-detection/policy-conflict-details`;

        return this.http.get(url, {
            headers,
            params,
            responseType: 'json',
        });
    }

    getPolicyAttributeConflict(
        networkId: string,
        identityRoles: string[],
        serviceRoles: string[],
        semantic = 'AnyOf',
        policyType = 'Bind'
    ): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        const normalizedIdentityRoles = this.normalizeAttributes(identityRoles);
        const normalizedServiceRoles = this.normalizeAttributes(serviceRoles);
        const identityAttributeValue = normalizedIdentityRoles.join(',');
        const serviceAttributeValue = normalizedServiceRoles.join(',');
        let params = new HttpParams()
            .set('networkId', networkId)
            .set('semantic', semantic || 'AnyOf')
            .set('policyType', policyType || 'Bind');

        if(identityAttributeValue) {
            params = params.set('identityAttribute', identityAttributeValue);
        }
        if(serviceAttributeValue) {
            params = params.set('serviceAttribute', serviceAttributeValue);
        }
        const protocol = this.environment.protocol || 'https';
        const url = `${protocol}://${this.environment.gatewayUrl}/conflict-detection/policy-conflict-precheck`;

        return this.http.get(url, {
            headers,
            params,
            responseType: 'json',
        });
    }

    private normalizeAttributes(attributes: string[] = []): string[] {
        return attributes
            .filter((attribute: string) => !isEmpty(attribute))
            .map((attribute: string) => {
                const trimmedAttribute = attribute.trim();
                if (trimmedAttribute.startsWith('#') || trimmedAttribute.startsWith('@')) {
                    return trimmedAttribute;
                }
                return `#${trimmedAttribute}`;
            });
    }

    get showApiCalls(): boolean {
        return this.featureService.powerUserEnabled;
    }
}
